import React, {useCallback, useEffect, useRef} from 'react';

import {ariaMenuOpenStateSignal} from './AriaMenu.signals';
import type {AriaMenuProps} from './AriaMenu.types';
import {InnerAriaMenuButton} from './InnerAriaMenuButton';

/**
 * AriaMenu component represents a dropdown menu. Menu items can open submenus in
 * place, rendering either a nested list of items or arbitrary React content,
 * with a back button to return to the previous level.
 * Unmounting an open menu reports a close through the signal and callback.
 */
export const AriaMenu: React.FC<AriaMenuProps> = ({
    items,
    button,
    isTriggerDisabled,
    closeMenuOnTriggerDisabled,
    renderButton,
    headerContent,
    onOpenChange: onOpenChangeProp,
    testId,
    colorScheme,
    popoverPlacement,
    popoverOffset,
    menuClassName,
    itemClassName,
    backButtonLabel,
    onGoBack,
    showSubMenuIndicator,
}) => {
    const isOpenRef = useRef(false);
    const onOpenChange = useCallback(
        (isOpen: boolean) => {
            isOpenRef.current = isOpen;
            ariaMenuOpenStateSignal.emit(isOpen);
            onOpenChangeProp?.(isOpen);
        },
        [onOpenChangeProp],
    );
    const onOpenChangeRef = useRef(onOpenChange);

    useEffect(() => {
        onOpenChangeRef.current = onOpenChange;
    }, [onOpenChange]);

    useEffect(
        () => () => {
            if (isOpenRef.current) {
                onOpenChangeRef.current(false);
            }
        },
        [],
    );

    return (
        <InnerAriaMenuButton
            items={items}
            button={button}
            isTriggerDisabled={isTriggerDisabled}
            closeMenuOnTriggerDisabled={closeMenuOnTriggerDisabled}
            renderButton={renderButton}
            headerContent={headerContent}
            onOpenChange={onOpenChange}
            testId={testId}
            colorScheme={colorScheme}
            popoverPlacement={popoverPlacement}
            popoverOffset={popoverOffset}
            menuClassName={menuClassName}
            itemClassName={itemClassName}
            backButtonLabel={backButtonLabel}
            onGoBack={onGoBack}
            showSubMenuIndicator={showSubMenuIndicator}
        />
    );
};
